// packages/web/src/routes/route.ts — B1: the structured route model that
// sits on top of useHashRoute's raw `#/path` string. Five tabs (Today,
// Auditions, [Knot], Projects, Career), each with its own sub-view state
// (a stage/phase filter, a section, or the Materials workshop reached only
// from Today) — all encoded into the hash path itself so a route is a
// plain string (shareable/bookmarkable/back-button-able), not hidden
// component state that a hash change can't reconstruct.
//
// The four Strip consumers (Auditions/Projects/Career/Today's Materials —
// see components/Strip.tsx) each own one of the id lists below; every
// unrecognised or missing sub-segment falls back to that list's first
// entry (see `parseRoute`), so a bare `/auditions` and `/auditions/all`
// are the same route.

export interface StripOption<Id extends string> {
  id: Id;
  label: string;
}

export type AuditionStage =
  | 'all'
  | 'self-submitted'
  | 'audition'
  | 'callback'
  | 'chemistry-read'
  | 'screen-test'
  | 'pinned'
  | 'booked'
  | 'archived';

export const AUDITION_STAGES: StripOption<AuditionStage>[] = [
  { id: 'all', label: 'All' },
  { id: 'self-submitted', label: 'Self-submitted' },
  { id: 'audition', label: 'Audition' },
  { id: 'callback', label: 'Callback' },
  { id: 'chemistry-read', label: 'Chemistry Read' },
  { id: 'screen-test', label: 'Screen Test' },
  { id: 'pinned', label: 'Pinned' },
  { id: 'booked', label: 'Booked' },
  { id: 'archived', label: 'Archived' },
];

export type ProjectPhase = 'all' | 'auditioned' | 'booked' | 'released';

export const PROJECT_PHASES: StripOption<ProjectPhase>[] = [
  { id: 'all', label: 'All' },
  { id: 'auditioned', label: 'Auditioned' },
  { id: 'booked', label: 'Booked' },
  { id: 'released', label: 'Released' },
];

export type CareerSection = 'money' | 'documents' | 'development' | 'stats';

export const CAREER_SECTIONS: StripOption<CareerSection>[] = [
  { id: 'money', label: 'Money' },
  { id: 'documents', label: 'Documents' },
  { id: 'development', label: 'Development' },
  { id: 'stats', label: 'Stats' },
];

export type MaterialsSection = 'me' | 'headshots' | 'resume' | 'reels';

export const MATERIALS_SECTIONS: StripOption<MaterialsSection>[] = [
  { id: 'me', label: 'This is me' },
  { id: 'headshots', label: 'Headshots' },
  { id: 'resume', label: 'Resume' },
  { id: 'reels', label: 'Reels' },
];

export type TopTab = 'today' | 'auditions' | 'projects' | 'career';

export type Route =
  | { screen: 'today' }
  | { screen: 'materials'; section: MaterialsSection }
  | { screen: 'auditions'; stage: AuditionStage }
  // B2: an audition's own detail screen — reached by tapping a row in the
  // Auditions list, NOT a Strip sub-view (no stage id represents "one
  // specific audition"), so it's its own Route case rather than folding
  // into the stage union above. Keeping it in the hash (`/auditions/
  // detail/:id`) rather than local component state is what makes it
  // share this file's own "a route is a plain string" property (shareable/
  // bookmarkable/back-button-able) with every other screen here.
  | { screen: 'auditionDetail'; id: string }
  | { screen: 'projects'; phase: ProjectPhase }
  // B3: a project's own phase-adaptive detail — reached by tapping a poster,
  // NOT a Strip sub-view, so (exactly like auditionDetail) it's its own
  // Route case rather than a phase id. `/projects/detail/:id`.
  | { screen: 'projectDetail'; id: string }
  // B3a provenance: the untouched source-document viewer. Reached from a
  // `.srclink` chip on either the audition breakdown or a booked project's
  // day-of card, so it carries `from` — the path to return to — encoded in
  // the hash itself (mirrors the demo's srcReturn/backFromSrc, but kept in
  // the URL so a source view stays shareable/bookmarkable/back-button-able
  // like every other route here). `/source/:id[/from/<return-path>]`.
  | { screen: 'source'; id: string; from?: string }
  | { screen: 'career'; section: CareerSection };

// options is always one of this file's own non-empty StripOption consts
// (AUDITION_STAGES etc) — the non-null assertion below just tells the
// compiler what that invariant already guarantees.
function findId<Id extends string>(options: StripOption<Id>[], value: string | undefined): Id {
  const match = value !== undefined ? options.find((o) => o.id === value) : undefined;
  return (match ?? options[0]!).id;
}

/** Parses a raw hash path (e.g. "/auditions/callback") into a Route. Any
 * unknown top segment (including "/") lands on Today — the same fallback
 * App.tsx's predecessor used for its 404-as-Today behaviour. */
export function parseRoute(path: string): Route {
  const segments = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  const [head, sub, third] = segments;

  switch (head) {
    case 'auditions':
      if (sub === 'detail' && third) return { screen: 'auditionDetail', id: third };
      return { screen: 'auditions', stage: findId(AUDITION_STAGES, sub) };
    case 'projects':
      if (sub === 'detail' && third) return { screen: 'projectDetail', id: third };
      return { screen: 'projects', phase: findId(PROJECT_PHASES, sub) };
    case 'source': {
      // /source/:id[/from/<return-path...>] — the return path is everything
      // after the "from" marker, rejoined into a leading-slash path.
      if (!sub) return { screen: 'today' };
      const fromIndex = segments.indexOf('from');
      const from = fromIndex >= 0 ? `/${segments.slice(fromIndex + 1).join('/')}` : undefined;
      return from ? { screen: 'source', id: sub, from } : { screen: 'source', id: sub };
    }
    case 'career':
      return { screen: 'career', section: findId(CAREER_SECTIONS, sub) };
    case 'materials':
      return { screen: 'materials', section: findId(MATERIALS_SECTIONS, sub) };
    default:
      return { screen: 'today' };
  }
}

/** The inverse of parseRoute — builds the hash path for a Route. Always
 * round-trips through parseRoute to the same Route (used by navigate()
 * callers instead of hand-building path strings). */
export function routePath(route: Route): string {
  switch (route.screen) {
    case 'today':
      return '/';
    case 'auditions':
      return route.stage === 'all' ? '/auditions' : `/auditions/${route.stage}`;
    case 'auditionDetail':
      return `/auditions/detail/${route.id}`;
    case 'projects':
      return route.phase === 'all' ? '/projects' : `/projects/${route.phase}`;
    case 'projectDetail':
      return `/projects/detail/${route.id}`;
    case 'source':
      // route.from is already a leading-slash path (e.g. "/projects/detail/x")
      // — appended after the "from" marker, its own leading slash absorbed.
      return route.from ? `/source/${route.id}/from${route.from}` : `/source/${route.id}`;
    case 'career':
      return route.section === 'money' ? '/career' : `/career/${route.section}`;
    case 'materials':
      return route.section === 'me' ? '/materials' : `/materials/${route.section}`;
  }
}

/** Which of the 5 bottom-nav slots a Route lights up. The Materials
 * workshop is reached only from Today's own strip, so it keeps Today lit —
 * entering it never reads as "leaving" the Today tab. */
export function topTab(route: Route): TopTab {
  switch (route.screen) {
    case 'today':
    case 'materials':
      return 'today';
    case 'auditions':
    case 'auditionDetail':
      return 'auditions';
    case 'projects':
    case 'projectDetail':
      return 'projects';
    case 'source':
      // The source viewer keeps its return screen's tab lit (mirrors the
      // demo's navHi reading srcReturn) — parse `from` to find it; a bare
      // source view (no return) is a projects-provenance read by default.
      return route.from ? topTab(parseRoute(route.from)) : 'projects';
    case 'career':
      return 'career';
  }
}

/** The bare default route each bottom-nav tab lands on. */
export function defaultRouteFor(tab: TopTab): Route {
  switch (tab) {
    case 'today':
      return { screen: 'today' };
    case 'auditions':
      return { screen: 'auditions', stage: 'all' };
    case 'projects':
      return { screen: 'projects', phase: 'all' };
    case 'career':
      return { screen: 'career', section: 'money' };
  }
}
